import React, {useEffect, useState} from "react";
import {Badge, Button, Divider, Dropdown, MenuProps, Modal, Popover, Space, Spin} from "antd";
import {langItems} from "../../utils/utils";
import {FormattedMessage, useIntl} from "react-intl";
import {
    BellOutlined,
    CaretDownFilled,
    CreditCardOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    LogoutOutlined,
    QuestionCircleOutlined,
    SafetyOutlined,
    UserOutlined
} from "@ant-design/icons";
import HelpLegalDrawer from './HelpLegalDrawer';
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import UserIcon from "../common/UserIcon";
import {isMobileOnly} from "react-device-detect";
import {getAuthenticationApi, getNotificationsApi, getStripeApi} from "../../api/apiInstances";
import {NotificationResponse} from "../../api-client";
import "../../assets/css/NotificationStyles.css"
import {Link, useLocation} from "react-router-dom";
import {tokenService} from "../../services/tokenService";
import {updateSettings} from "../../services/settingsService";
import logger from "../../utils/logger";
import OrgSwitcher from "./OrgSwitcher";
import {HelpSectionContext} from "../../context/HelpSectionContext";

const getContextualHelpPath = (pathname: string, helpSlug: string | null): string => {
    // Component-registered help section takes priority (e.g. Messaging tab)
    if (helpSlug) return `/help/${helpSlug}`;
    if (/^\/(organizations?|organization)/.test(pathname)) return '/help/multi-organization';
    if (/^\/medical\/staff/.test(pathname)) return '/help/invitations-and-portal-access';
    return '/help';
};

// Map backend NotificationType enum values to i18n message keys.
// message field contains JSON params: { patient_name, date, old_date? }
const APPOINTMENT_NOTIF_I18N: Record<string, string> = {
    APPOINTMENT_CONFIRMED:   'notif_appointment_confirmed',
    APPOINTMENT_RESCHEDULED: 'notif_appointment_rescheduled',
    APPOINTMENT_CANCELLED:   'notif_appointment_cancelled',
    PATIENT_DELETED:         'notif_patient_deleted',
    STAFF_DEACTIVATED:       'notif_staff_deactivated',
    STAFF_ACTIVATED:         'notif_staff_activated',
};

// Notification types where clicking should never navigate to a deleted/stale resource.
// These render without a "here" link; the whole item is clickable to mark as read.
const NO_LINK_NOTIF_TYPES = new Set(['PATIENT_DELETED', 'APPOINTMENT_CANCELLED']);

export const RightHeaderMenu = () => {

    const location = useLocation();
    const intl = useIntl();

    const {locale, setLocale, principal} = React.useContext(DentobookApplicationContext);
    const {helpSlug} = React.useContext(HelpSectionContext);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);
    const [helpLegalOpen, setHelpLegalOpen] = useState<boolean>(false);
    const [visible, setVisible] = useState(false);
    const [languageChanging, setLanguageChanging] = useState(false);
    const [notificationsList, setNotificationsList] = useState<NotificationResponse[]>([]);
    const notificationsApiFactory = getNotificationsApi();
    const numNotifications = notificationsList.filter(n => !n.isRead).length;
    const [isExpanded, setIsExpanded] = useState(false);
    const [stripeEnabled, setStripeEnabled] = useState(false);

    const renderNotificationText = (notification: NotificationResponse): string => {
        const i18nKey = APPOINTMENT_NOTIF_I18N[notification.type ?? ''];
        if (i18nKey) {
            try {
                const params = notification.message ? JSON.parse(notification.message) : {};
                return intl.formatMessage({id: i18nKey}, params);
            } catch {
                // message is not JSON (old row) — fall through to title
            }
        }
        return notification.title || '';
    };

    const fetchNotifications = () => {
        if (principal?.user) {
            notificationsApiFactory.fetchMyNotifications()
                .then(response => {
                    setNotificationsList(response.data);
                })
                .catch(error => logger.error("Error fetching notifications:", error));
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [location]);

    useEffect(() => {
        getStripeApi().isStripeEnabled()
            .then(res => setStripeEnabled(res.data?.enabled ?? false))
            .catch(() => setStripeEnabled(false));
    }, []);


    const handleNotificationClick = (
        entityId: string,
        notificationId: string,
        entityType?: string,
        notifType?: string,
        messageJson?: string,
        isRead?: boolean,
    ) => {
        notificationsApiFactory.markNotificationAsRead(notificationId)
            .then(() => {
                if (notifType === 'PATIENT_DELETED') {
                    // Patient is gone — mark read only, never navigate.
                    return;
                }
                if (notifType === 'APPOINTMENT_CANCELLED') {
                    if (!isRead) {
                        // First click on an unread cancelled notification:
                        // store a one-time freed-slot flash so CalendarPage highlights the slot.
                        try {
                            const params = messageJson ? JSON.parse(messageJson) : {};
                            if (params.start_iso) {
                                sessionStorage.setItem('dentobook_freed_slot', JSON.stringify({
                                    startIso: params.start_iso,
                                    endIso: params.end_iso ?? null,
                                    clinicId: params.clinic_id ?? null,
                                }));
                            }
                        } catch {
                            // ignore parse errors — flash is best-effort
                        }
                        window.location.href = '/calendar';
                    }
                    // Already read: mark-read only, do not navigate to deleted event.
                    return;
                }
                // Live event notifications: deep-link into calendar.
                if (entityType === 'calendar_event' || entityType === 'appointment') {
                    window.location.href = '/calendar?eventId=' + entityId;
                } else if (entityType === 'medical_staff') {
                    window.location.href = '/medical/staff/' + entityId;
                } else {
                    window.location.href = '/patients/' + entityId;
                }
            })
            .catch((error: any) => {
                logger.error("Error marking notification as read: ", error);
            });
    }

    const handleVisibility = () => {
        setVisible(!visible);
    };

    const sortedNotifications = [...notificationsList].sort((a, b) => {
        const dateA = new Date(a.createdAt ?? 0);
        const dateB = new Date(b.createdAt ?? 0);
        return dateB.getTime() - dateA.getTime();
    });

    const content = notificationsList.length === 0 ? (
        <div className="notification-list">
            <div className="notification-item">
                <span>{<FormattedMessage id="no_notifications"/>}</span>
            </div>
        </div>
    ) : (
        <div className="notification-list">
            {(isExpanded ? sortedNotifications : sortedNotifications.slice(0, 3)).map((notification, index) => {
                const noLink = NO_LINK_NOTIF_TYPES.has(notification.type ?? '');
                const doClick = () => {
                    if (notification.id && notification.relatedEntityId) {
                        handleNotificationClick(
                            notification.relatedEntityId,
                            notification.id,
                            notification.relatedEntityType ?? undefined,
                            notification.type ?? undefined,
                            notification.message ?? undefined,
                            notification.isRead ?? undefined,
                        );
                    }
                };
                return (
                    <div key={notification.id}
                         className={`notification-item ${!notification.isRead ? 'notification-unread' : ''} ${index !== sortedNotifications.length - 1 ? 'with-divider' : ''}`}
                         {...(noLink ? {onClick: doClick, style: {cursor: 'pointer'}} : {})}>
                        <div className="notification-content">
                            <div className="info-icon custom-icon">
                                <InfoCircleOutlined className="anticon"/>
                            </div>
                            <span>{renderNotificationText(notification) || <FormattedMessage id="new_patient"/>}{' '}</span>
                            {!noLink && (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (notification.id && notification.relatedEntityId) {
                                            handleNotificationClick(
                                                notification.relatedEntityId,
                                                notification.id,
                                                notification.relatedEntityType ?? undefined,
                                                notification.type ?? undefined,
                                                notification.message ?? undefined,
                                                notification.isRead ?? undefined,
                                            );
                                        } else {
                                            logger.error("Either notification.id or notification.relatedEntityId is undefined.");
                                        }
                                    }}
                                    className="notification-link"><FormattedMessage id="here"/></a>
                            )}
                            <div className="notification-date">
                                {formatDate(notification.createdAt)}
                            </div>
                        </div>
                    </div>
                );
            })}
            {sortedNotifications.length > 3 && !isExpanded && (
                <button
                    className="expand-notification-list plain-button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(true);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsExpanded(true);
                        }
                    }}
                >
                    <FormattedMessage id="show_all"/> {notificationsList.length} <FormattedMessage id="notifications"/>
                </button>
            )}
            {isExpanded && (
                <button
                    className="collapse-notification-list plain-button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsExpanded(false);
                        }
                    }}
                >
                    <FormattedMessage id="collapse"/>
                </button>
            )}
            {notificationsList.some(n => !n.isRead) && (
                <div style={{display: 'flex', justifyContent: 'flex-end', padding: '4px 0 0 0'}}>
                    <button
                        className="plain-button"
                        style={{fontSize: 12, color: '#1677ff', cursor: 'pointer'}}
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            notificationsApiFactory.markAllNotificationsAsRead()
                                .then(() => fetchNotifications())
                                .catch((err: any) => logger.error("Error marking all as read:", err));
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                notificationsApiFactory.markAllNotificationsAsRead()
                                    .then(() => fetchNotifications())
                                    .catch((err: any) => logger.error("Error marking all as read:", err));
                            }
                        }}
                    >
                        <FormattedMessage id="mark_all_read"/>
                    </button>
                </div>
            )}
        </div>
    );

    const promptLogout = () => {
        setLogoutDialogOpen(true);
    };

    const handleLogoutOk = async () => {
        setLogoutDialogOpen(false);
        try {
            // Call logout endpoint to track logout event in audit log
            const authApi = getAuthenticationApi();
            await authApi.logout();
        } catch (err) {
            // Log error but continue with logout even if API call fails
            logger.error('Failed to track logout event:', err);
        } finally {
            // Clear JWT tokens from sessionStorage
            tokenService.clearTokens();
            // Redirect to login page
            window.location.href = "/login";
        }
    };

    const handleLogoutCancel = () => {
        setLogoutDialogOpen(false);
    };

    const localeItems: MenuProps['items'] = langItems.map(l => (
        {
            key: l.label,
            label: l.label,
            onClick: async () => {
                try {
                    // Show loading overlay immediately to prevent UI flicker
                    setLanguageChanging(true);

                    // Update backend first
                    await updateSettings({ language: l.value });

                    // Update frontend state
                    setLocale(l.value);
                    localStorage.setItem('dentobook-lang', l.value);

                    // Small delay to ensure overlay is visible before refresh
                    setTimeout(() => {
                        // Refresh page to reload all locale-dependent data
                        // This ensures all treatments, statuses, and other display names update correctly
                        window.location.reload();
                    }, 100);
                } catch (error) {
                    logger.error('Failed to update language setting:', error);
                    // Show overlay even on error
                    setLanguageChanging(true);

                    // Still update locally and refresh even if backend fails
                    setLocale(l.value);
                    localStorage.setItem('dentobook-lang', l.value);

                    setTimeout(() => {
                        window.location.reload();
                    }, 100);
                }
            }
        })
    );

    const userItems: MenuProps['items'] = [
        {
            key: '1',
            label: (
                <Space className="mt-2">
                    <div>
                        <UserIcon size="xs" rounded={true}/>
                    </div>
                    <Space direction="vertical" className="user-items-header">
                        <span
                            className="dentobook-content font-14 font-weight-500 font-family-roboto">
                            {principal?.user?.firstName} {principal?.user?.lastName}
                        </span>
                        <span className="font-10 font-weight-400 font-family-roboto"><FormattedMessage
                            id={principal?.user?.role}/></span>
                    </Space>
                </Space>
            ),
        },
        {
            key: '1.5',
            label: (
                <Link to="/profile" className="font-family-roboto">
                    <FormattedMessage id="my_profile"/>
                </Link>
            ),
            icon: <UserOutlined style={{fontSize: 16}}/>
        },
        ...(stripeEnabled ? [{
            key: '2',
            label: (
                <Link to="/billing" className="font-family-roboto">
                    <FormattedMessage id="billing" />
                </Link>
            ),
            icon: <CreditCardOutlined style={{fontSize: 16}}/>
        }] : []),
        {
            key: '2.5',
            label: (
                <Link to={getContextualHelpPath(location.pathname, helpSlug)} className="font-family-roboto">
                    <FormattedMessage id="help_nav" />
                </Link>
            ),
            icon: <QuestionCircleOutlined style={{fontSize: 16}}/>
        },
        {
            key: '2.8',
            label: (
                <span className="font-family-roboto"><FormattedMessage id="help_legal"/></span>
            ),
            icon: <SafetyOutlined style={{fontSize: 16}}/>,
            onClick: () => setHelpLegalOpen(true),
        },
        {
            key: '3',
            label: (
                <span className="font-family-roboto"><FormattedMessage id="logout"/></span>
            ),
            icon: <LogoutOutlined style={{fontSize: 16}}/>,
            onClick: () => {
                promptLogout();
            }
        }
    ];

    return (
        <div className="nav-items">
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <Dropdown
                    menu={{items: localeItems}}
                    className="langs-dropdown">
                    <button className="dropdown-text dentobook-link font-family-roboto plain-button"
                            onKeyDown={e => e.preventDefault()}
                            onClick={e => e.preventDefault()}>
                        <Space>
                            {langItems.find(l => l.value === locale)?.label || locale.toUpperCase()}
                            <CaretDownFilled width={100}/>
                        </Space>
                    </button>
                </Dropdown>
                {principal?.user ?
                    <Popover
                        className="popover-style"
                        content={content}
                        trigger="hover"
                        defaultOpen={visible}
                        afterOpenChange={handleVisibility}
                        placement="bottomLeft"
                    >
                        <Badge count={numNotifications} color="blue" style={{marginRight: 12}}>
                            <BellOutlined className="notifications" onClick={handleVisibility}/>
                        </Badge>
                    </Popover>
                    : null}
            {principal?.user ? <OrgSwitcher /> : null}
            </div>
            {principal?.user ?
                <>
                    <Divider type="vertical" className="divider"/>
                    <Dropdown menu={{items: userItems}}
                              rootClassName="langs-dropdown-root"
                              className="langs-dropdown"
                              overlayClassName="user-dropdown">
                        <button className="dropdown-text dentobook-link plain-button"
                                onKeyDown={e => e.preventDefault()}
                                onClick={e => e.preventDefault()}>
                            <Space>
                                {principal?.medicalPerson?.avatarUrl ?
                                    <img className="user-avatar-img"
                                         width={10}
                                         src={principal?.medicalPerson?.avatarUrl}
                                         alt="avatar"/> :
                                    <svg viewBox="64 64 896 896" focusable="false" data-icon="user" width="1em"
                                         height="1em"
                                         fill="currentColor" aria-hidden="true">
                                        <path
                                            d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.3-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.8-204.3 56.7-56.7 132-87.9 212.2-87.9s155.5 31.2 212.2 87.9C779 752.7 810 825 812 902.2c.1 4.4 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z"></path>
                                    </svg>
                                }
                                <span
                                    className="ml-4 font-14 font-family-roboto">{principal?.user?.firstName} {principal?.user?.lastName?.charAt(0) + '.'}</span>
                            </Space>
                        </button>
                    </Dropdown>
                </>
                : null}
            <HelpLegalDrawer open={helpLegalOpen} onClose={() => setHelpLegalOpen(false)}/>
            <Modal
                transitionName=""
                className="logout-modal"
                open={logoutDialogOpen}
                footer={null}
                width={"100vw"}
                style={{height: '100vh'}}
                closable={false}>
                <div className="mt-10" style={{
                    display: 'flex',
                    width: '100vw',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img src={require('../../assets/logo.png')} height={90} alt=""/>
                    <p className="font-family-roboto font-weight-500 font-24 text-align-center mt-4"
                       style={!isMobileOnly ? {width: '30%'} : {width: '100%'}}>
                        <FormattedMessage id="logoutConfirm"/>
                    </p>
                    <Space className="mt-4">
                        <Button className="font-16 font-weight-500 modal-btn-large"
                                onClick={handleLogoutCancel}><FormattedMessage
                            id="cancel"/></Button>
                        <Button className="font-16 font-weight-500 modal-btn-large" type="primary"
                                onClick={handleLogoutOk}><FormattedMessage
                            id="logout"/></Button>
                    </Space>
                </div>
            </Modal>

            {/* Full-screen loading overlay for language change */}
            {languageChanging && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    backdropFilter: 'blur(4px)'
                }}>
                    <Spin
                        size="large"
                        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                        tip={<span style={{ fontSize: 16, marginTop: 16 }}>
                            <FormattedMessage id="loading" />...
                        </span>}
                    />
                </div>
            )}
        </div>
    )
}

const formatDate = (createdAtString: any) => {
    const date = new Date(createdAtString);
    return `${date.toLocaleTimeString().substring(0, 5)} ${date.toLocaleDateString()}`;
};

export default RightHeaderMenu;